// src/components/Notes/ShareDialog.tsx
import React, { useState, useEffect } from 'react';
import { X, Search, UserPlus, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { EncryptionService } from '../../utils/encryption';

const encryption = new EncryptionService();

interface ShareDialogProps {
  isOpen: boolean;
  note: any;
  userId: string;
  dek?: CryptoKey | null;
  onClose: () => void;
  onShareSuccess: () => void;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  note,
  userId,
  dek,
  onClose,
  onShareSuccess,
}) => {
  console.log('🔍 ShareDialog 收到的 note:', note);
  console.log('🔍 ShareDialog 收到的 note 類型:', typeof note);
  console.log('🔍 ShareDialog 收到的 note 是否為 null:', note === null);
  console.log('🔍 ShareDialog 收到的 note 是否為 undefined:', note === undefined);
	
  const noteId = note?.id;
  const noteTitle = note?.title;
  const encryptedNoteKey = note?.noteKey;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // ✅ 改為陣列，支援多選
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUsers([]);
      setError('');
    }
  }, [isOpen]);

  const handleSearch = async () => {
    const query = searchQuery.trim();
    setIsSearching(true);
    setError('');

    try {
      const searchParam = query || '*';
      const response = await fetch(`/api/users/search?q=${encodeURIComponent(searchParam)}`, {
        headers: { 'X-User-Id': userId }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '搜尋失敗');
      }

      setSearchResults(data.users || []);
      if (data.users?.length === 0) {
        toast('找不到此使用者', { icon: '📭' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '搜尋失敗');
    } finally {
      setIsSearching(false);
    }
  };

  // ✅ 切換用戶選擇（多選）
  const toggleUserSelection = (user: any) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  // ✅ 移除已選用戶
  const removeSelectedUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  // ✅ 分享給所有選中的用戶
	const handleShare = async () => {
		// ✅ 1. 檢查筆記物件
		if (!note) {
			toast.error('筆記資料載入中，請稍後再試');
			return;
		}

		// ✅ 2. 檢查是否選擇了用戶
		if (selectedUsers.length === 0) {
			toast.error('請選擇要分享的對象');
			return;
		}

		// ✅ 3. 檢查 DEK
		if (!dek) {
			toast.error('加密金鑰未初始化，請重新登入');
			return;
		}

		// ✅ 4. 檢查 encryptedNoteKey
		if (!encryptedNoteKey) {
			toast.error('筆記金鑰載入中，請稍後再試');
			return;
		}

		// ✅ 5. 檢查 encryptedNoteKey 長度
		if (encryptedNoteKey.length === 0) {
			toast.error('筆記金鑰尚未建立，請先儲存筆記');
			return;
		}

		setIsSharing(true);
		setError('');
		
		let successCount = 0;
		let failCount = 0;
		let successNames: string[] = [];
		let failNames: string[] = [];

		try {
			for (const user of selectedUsers) {
				try {
					// 1. 解密 Note Key
					const noteKeyBuffer = await encryption.decryptNoteKeyWithDEK(encryptedNoteKey, dek);
					const noteKeyBase64 = encryption.arrayBufferToBase64(noteKeyBuffer);

					// 2. 生成一次性分享密碼
					const sharePassword = encryption.generateSharePassword();

					// 3. 用分享密碼加密 Note Key
					const passwordKey = await encryption.deriveKeyFromPassword(sharePassword);
					const encryptedNoteKeyForShare = await encryption.encryptWithNoteKey(
						noteKeyBase64,
						passwordKey
					);

					// 4. 發送到後端
					const response = await fetch('/api/notes/share', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-User-Id': userId,
						},
						body: JSON.stringify({
							noteId,
							targetUsername: user.username,
							permission,
							title: noteTitle,
							encryptedNoteKey: encryptedNoteKeyForShare,
							sharePassword,
						}),
					});

					const data = await response.json();

					if (!response.ok) {
						if (response.status === 409) {
							if (data.status === 'pending') {
								successCount++;
								successNames.push(user.username);
								continue;
							} else if (data.status === 'accepted') {
								successCount++;
								successNames.push(user.username);
								continue;
							}
						}
						throw new Error(data.error || `分享給 ${user.username} 失敗`);
					}

					successCount++;
					successNames.push(user.username);

				} catch (err) {
					failCount++;
					failNames.push(user.username);
					console.error(`分享給 ${user.username} 失敗:`, err);
				}
			}

			// ✅ 總結 Toast
			if (successCount > 0 && failCount === 0) {
				toast.success(`✅ 已成功發送 ${successCount} 個分享邀請！`);
				onShareSuccess();
				onClose();
			} else if (successCount > 0 && failCount > 0) {
				toast.warning(`⚠️ 成功 ${successCount} 個，失敗 ${failCount} 個`);
				onShareSuccess();
				onClose();
			} else {
				toast.error('所有分享邀請都失敗了，請重試');
			}

		} catch (err) {
			setError(err instanceof Error ? err.message : '分享失敗');
			toast.error(err instanceof Error ? err.message : '分享失敗');
		} finally {
			setIsSharing(false);
		}
	};

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[150] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* 標題欄 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            📤 分享筆記
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 筆記標題 */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
            📝 {noteTitle}
          </p>
        </div>

        {/* 內容 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* 搜尋區 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              搜尋使用者（可選多個）
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="請輸入使用者名稱..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                disabled={isSearching}
              />
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1 text-sm"
              >
                <Search className="w-4 h-4" />
                {isSearching ? '搜尋中...' : '搜尋'}
              </button>
            </div>
          </div>

          {/* 搜尋結果 - 複選 */}
          {searchResults.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-40 overflow-y-auto">
              {searchResults.map((user) => {
                const isSelected = selectedUsers.some(u => u.id === user.id);
                return (
                  <button
                    key={user.id}
                    onClick={() => toggleUserSelection(user)}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{user.username}</span>
                    {isSelected && (
                      <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* 已選使用者 */}
          {selectedUsers.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                已選取 {selectedUsers.length} 位使用者：
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <span
                    key={user.id}
                    className="inline-flex items-center gap-1 bg-white dark:bg-gray-700 px-2 py-1 rounded-full text-sm text-gray-700 dark:text-gray-300"
                  >
                    {user.username}
                    <button
                      onClick={() => removeSelectedUser(user.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 權限選擇 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              權限
            </label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  name="permission"
                  value="read"
                  checked={permission === 'read'}
                  onChange={() => setPermission('read')}
                  className="w-4 h-4 text-blue-600"
                />
                📖 唯讀
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  name="permission"
                  value="write"
                  checked={permission === 'write'}
                  onChange={() => setPermission('write')}
                  className="w-4 h-4 text-blue-600"
                />
                ✏️ 可編輯
              </label>
            </div>
          </div>

          {/* 錯誤訊息 */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* 底部按鈕 */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
          >
            取消
          </button>
          <button
            onClick={handleShare}
            disabled={selectedUsers.length === 0 || isSharing}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            {isSharing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                發送中...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                發送給 {selectedUsers.length} 位使用者
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};